import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import Lightbox from '../components/Lightbox';
import { fetchLocalizedCollection } from '../lib/cms';

export default function UsedPressesPage() {
  const [machines, setMachines] = useState([]);
  const [pageTitle, setPageTitle] = useState('Brugte maskiner');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchLocalizedCollection('brugte_maskiner', 'da').then((items) => {
      if (items && items.length) {
        setMachines(items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
      }
    });

    fetchLocalizedCollection('ydelser_produkter', 'da').then((items) => {
      const match = items.find(
        (i) => i.currentSlug === 'presser-2' || i.currentSlug === 'brugte-maskiner' || i.id === '285aefc2-89ea-432b-95c7-33bde3684b54'
      );
      if (match?.titel) {
        setPageTitle(match.titel);
      }
    });
  }, []);

  const galleryImages = machines.map((m) => {
    const url = Array.isArray(m.billede) ? m.billede[0] : m.billede;
    return { url, title: m.titel };
  });

  return (
    <>
      <Helmet>
        <title>{pageTitle} - POVL MØLLERS MASKINFABRIK A/S</title>
        <meta
          name="description"
          content={`${pageTitle} – driftssikkerhed med omtanke. Renoverede og gennemgåede excenterpresser og coil-afviklere med dokumentation og garanti.`}
        />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content="Med mere end 60 års erfaring giver vi brugte maskiner og presser nyt liv – så de kan skabe værdi i din produktion i mange år fremover."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <Link to="/presser" className="hover:text-[#006368] transition">
            Presser
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{pageTitle}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              Kvalitetsmaskiner
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {pageTitle}
            </h1>
            <p className="mt-2 text-xl text-[#0d8086] font-semibold">
              driftssikkerhed med omtanke
            </p>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              Hos <strong>PMB</strong> ser vi værdien i hver eneste maskine. Med mere end 60 års erfaring giver vi brugte presser nyt liv – så de kan skabe værdi i din produktion i mange år fremover.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#maskinoversigt"
                className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm tracking-wide transition shadow-sm flex items-center gap-2"
              >
                <span>Se aktuelle maskiner</span>
                <i className="fa-solid fa-arrow-down text-xs" />
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition whitespace-nowrap"
              >
                Søger du en specifik model?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Værdipunkter fra scrape */}
      <section className="py-16 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-stone-900 mb-8">
            Når du vælger en brugt presser hos os, får du en maskine der er:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-shield-check text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Gennemgået & testet</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Renoveret og gennemgået efter de samme standarder som nye presser.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-file-lines text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Fuld dokumentation</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Leveret med brugsanvisning, dokumentation og gennemskuelig serviceplan.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-screwdriver-wrench text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Fabriksrenovering</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Mulighed for fabriksrenovering eller en driftssikker, lovlig løsning tilpasset jeres behov.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-gears text-lg" />
              </div>
              <h3 className="font-bold text-stone-900 text-base">Reservedele & tilpasning</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                Tryghed i, at reservedele, opdateringer og eventuelt ekstraudstyr altid kan tilpasses.
              </p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-white rounded-lg border border-stone-200 text-sm text-stone-700 leading-relaxed">
            <p>
              Vi kan også tilpasse pressen med nyt udstyr eller overfladebehandling, så den passer til din produktion – og levere den installeret og klar til værktøjsafprøvning.
            </p>
            <p className="mt-2 font-semibold text-stone-900">
              Kort sagt: En brugt presser fra PMB er ikke bare en genbrugsmaskine, men en investering i driftssikkerhed, tryghed og produktivitet.
            </p>
          </div>
        </div>
      </section>

      {/* Maskinoversigt */}
      <section id="maskinoversigt" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
                Aktuelt lager
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-1">
                Udvalgte brugte maskiner
              </h2>
            </div>
            <p className="text-sm text-stone-500">
              {machines.length} maskiner på lager eller under renovering
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {machines.map((machine, index) => {
              const imgUrl = Array.isArray(machine.billede) ? machine.billede[0] : machine.billede;
              return (
                <div
                  key={machine.id || index}
                  className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm flex flex-col justify-between hover:border-[#006368] transition duration-200"
                >
                  {/* Machine Image */}
                  <div
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                    className="relative h-72 w-full bg-stone-100 flex items-center justify-center p-4 cursor-pointer group border-b border-stone-200"
                    role="button"
                    tabIndex={0}
                    aria-label={`Forstør billede af ${machine.titel}`}
                  >
                    <span className="absolute top-3 left-3 px-2 py-1 rounded bg-[#006368] text-white text-xs font-bold tracking-wide">
                      {machine.maskintype}
                    </span>
                    <CmsImage
                      src={imgUrl}
                      alt={machine.titel}
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width={500}
                    />
                    <div className="absolute inset-0 bg-[#006368]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <span className="bg-[#1a2024]/90 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5">
                        <i className="fa-solid fa-magnifying-glass-plus" />
                        <span>Se stort foto</span>
                      </span>
                    </div>
                  </div>

                  {/* Specifications list */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-stone-900">{machine.titel}</h3>
                      <p className="text-xs text-stone-500 mt-0.5">Producent: {machine.producent}</p>
                      {machine.beskrivelse && (
                        <p className="mt-2 text-xs text-stone-600 leading-relaxed">{machine.beskrivelse}</p>
                      )}

                      <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        {machine.model && (
                          <div>
                            <span className="text-stone-400 block">Typebetegnelse:</span>
                            <strong className="text-stone-800">{machine.model}</strong>
                          </div>
                        )}
                        {machine.fabrikationsnr && (
                          <div>
                            <span className="text-stone-400 block">Fabrikations nr:</span>
                            <strong className="text-stone-800">{machine.fabrikationsnr}</strong>
                          </div>
                        )}
                        {machine.aarstal && (
                          <div>
                            <span className="text-stone-400 block">Fabrikations år:</span>
                            <strong className="text-stone-800">{machine.aarstal}</strong>
                          </div>
                        )}
                        {machine.pressekraft && (
                          <div>
                            <span className="text-stone-400 block">Pressekraft:</span>
                            <strong className="text-[#006368]">{machine.pressekraft}</strong>
                          </div>
                        )}
                        {machine.vaegt && machine.vaegt !== '-' && (
                          <div>
                            <span className="text-stone-400 block">Pressens vægt:</span>
                            <strong className="text-stone-800">{machine.vaegt}</strong>
                          </div>
                        )}
                        {machine.slaglaengde && machine.slaglaengde !== '-' && (
                          <div>
                            <span className="text-stone-400 block">Slaglængde:</span>
                            <strong className="text-stone-800">{machine.slaglaengde}</strong>
                          </div>
                        )}
                        {machine.hastighed && machine.hastighed !== '-' && (
                          <div>
                            <span className="text-stone-400 block">Hastighed:</span>
                            <strong className="text-stone-800">{machine.hastighed}</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-stone-100">
                      <Link
                        to={`/kontakt?presser=${encodeURIComponent(machine.titel)}`}
                        className="w-full py-2.5 px-4 rounded bg-[#006368] hover:bg-[#004d51] text-white text-xs font-semibold tracking-wide transition flex items-center justify-center gap-2 shadow-2xs"
                      >
                        <i className="fa-solid fa-envelope" />
                        <span>Forespørg på denne maskine</span>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hvorfor vælge PMB presser */}
      <section className="py-20 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
                Din tryghed
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-2">
                Hvorfor vælge PMB–presser?
              </h2>
              <p className="mt-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                Når du vælger en presser fra PMB, vælger du en partner, der forstår dine udfordringer. Hver presser bliver grundigt gennemgået, opgraderet og gjort klar til driftssikker performance.
              </p>

              <ul className="mt-6 space-y-3 text-sm text-stone-800 font-medium">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Skræddersyede løsninger til ethvert behov</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Høj ydeevne og stabil produktion</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Fremtidssikret teknologi med lang levetid</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Service og support i hele Danmark</span>
                </li>
              </ul>

              <div className="mt-8">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white text-sm font-semibold transition shadow-sm"
                >
                  <span>Kontakt os for dialog om brugte maskiner</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg overflow-hidden border border-stone-200 bg-white p-2 shadow-2xs">
                <CmsImage
                  src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presse-pmb-2008-oppefra.webp"
                  alt="Presse PMB 2008 oppefra"
                  className="w-full h-48 object-cover rounded"
                  width={400}
                />
                <p className="text-[11px] text-stone-500 mt-2 text-center">Gennemgang på værksted</p>
              </div>
              <div className="rounded-lg overflow-hidden border border-stone-200 bg-white p-2 shadow-2xs">
                <CmsImage
                  src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pmb-presse.webp"
                  alt="PMB presse"
                  className="w-full h-48 object-cover rounded"
                  width={400}
                />
                <p className="text-[11px] text-stone-500 mt-2 text-center">Driftssikker performance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={galleryImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
